import React from 'react';
import {
    Link as LinkIcon, MoreVertical, Trash2, RefreshCw, ExternalLink,
    StickyNote, Type, Heart, Share2, Users, AlertTriangle, Edit2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuTrigger, DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import { ProcessingCardOverlay } from '../../components/ProcessingIndicator';
import { ReminderBadge } from '../../components/ReminderPicker';
import {
    isHebrew, formatDate, getContentIcon, hasQualityIssues,
    getQualityMessage, getStatusBadge, getCategoryIcon,
} from './utils';

// ─── BookmarkCard ──────────────────────────────────────────────────
// The bookmark tile rendered in every dashboard grid cell. Extracted
// verbatim from Dashboard.js — no visual change. All handlers arrive
// as props so the card stays presentational and re-renders cheaply.
//
// Props:
//   bookmark          — the bookmark doc
//   isProcessing      — true while the pipeline is still working
//   hasReminder       — from the bookmarkReminders map
//   categories        — full user categories list (for chips)
//   onNavigate(path)  — internal navigation to /bookmark/:id (etc.)
//   onReprocess(id)
//   onToggleFavorite(evt, id)
//   onShare(evt, bookmark)
//   onDelete(id)
export function BookmarkCard({
    bookmark,
    isProcessing,
    hasReminder,
    categories,
    onNavigate,
    onReprocess,
    onToggleFavorite,
    onShare,
    onDelete,
    onUpgradeRequest,
}) {
    const bmCats = React.useMemo(() => {
        const catIds = bookmark.category_ids || (bookmark.category_id ? [bookmark.category_id] : []);
        return catIds.map((id) => categories.find((c) => c.category_id === id)).filter(Boolean);
    }, [bookmark.category_ids, bookmark.category_id, categories]);

    return (
        <div
            className={`bg-white border border-border/80 rounded-2xl overflow-hidden transition-shadow hover:shadow-lg group cursor-pointer shadow-card ${isProcessing ? 'ring-1 ring-primary/20' : ''}`}
            onClick={() => onNavigate(`/bookmark/${bookmark.bookmark_id}`)}
            data-testid={`bookmark-card-${bookmark.bookmark_id}`}
        >
            {/* Thumbnail area - shows processing overlay OR thumbnail, not both */}
            {isProcessing ? (
                <ProcessingCardOverlay
                    stage={bookmark.processing_stage || 'extracting'}
                    updatedAt={bookmark.updated_at}
                    onRetry={() => onReprocess(bookmark.bookmark_id)}
                    url={bookmark.url}
                />
            ) : bookmark.thumbnail ? (
                <div className="aspect-video relative overflow-hidden bg-secondary">
                    <ReminderBadge hasReminder={hasReminder} />
                    <img
                        src={bookmark.thumbnail}
                        alt={bookmark.title}
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                        onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                    />
                    <div className="absolute top-2 left-2">
                        <div className="w-7 h-7 rounded-lg bg-white/90 backdrop-blur flex items-center justify-center shadow-sm">
                            {getContentIcon(bookmark.content_type)}
                        </div>
                    </div>
                    {bookmark.content_type === 'video' && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none hidden" data-testid={`play-overlay-${bookmark.bookmark_id}`}>
                        </div>
                    )}
                    {/* Top-right action buttons - Favorite + Share.
                        Both buttons stop click propagation so they don't
                        trigger the parent card navigation. */}
                    <div className="absolute top-2 right-2 flex items-center gap-1.5">
                        <button
                            type="button"
                            onClick={(e) => onToggleFavorite(e, bookmark.bookmark_id)}
                            className="w-8 h-8 rounded-full bg-white/90 backdrop-blur hover:bg-white flex items-center justify-center shadow-sm transition-transform active:scale-90"
                            data-testid={`bookmark-favorite-${bookmark.bookmark_id}`}
                            aria-label={bookmark.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
                        >
                            <Heart className={`w-4 h-4 transition-colors ${bookmark.is_favorite ? 'fill-red-500 text-red-500' : 'text-zinc-700'}`} />
                        </button>
                        <button
                            type="button"
                            onClick={(e) => onShare(e, bookmark)}
                            className="w-8 h-8 rounded-full bg-white/90 backdrop-blur hover:bg-white flex items-center justify-center shadow-sm transition-transform active:scale-90"
                            data-testid={`bookmark-share-${bookmark.bookmark_id}`}
                            aria-label="Share bookmark"
                        >
                            <Share2 className="w-4 h-4 text-zinc-700" />
                        </button>
                    </div>
                </div>
            ) : null}

            <div className="p-3 sm:p-4">
                {/* Title & Menu */}
                <div className="flex items-start justify-between gap-2 mb-2">
                    <h3
                        className="font-semibold text-sm line-clamp-2 group-hover:text-primary transition-colors"
                        dir={isHebrew(bookmark.title) ? 'rtl' : 'ltr'}
                    >
                        {bookmark.title}
                    </h3>
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground flex-shrink-0">
                                    <MoreVertical className="w-4 h-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                {bookmark.url && (
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); window.open(bookmark.url, '_blank'); }}>
                                        <ExternalLink className="w-4 h-4 mr-2" /> Open
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={(e) => onShare(e, bookmark)} data-testid={`share-menu-${bookmark.bookmark_id}`}>
                                    <Share2 className="w-4 h-4 mr-2" /> Share
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onReprocess(bookmark.bookmark_id); }}>
                                    <RefreshCw className="w-4 h-4 mr-2" /> Reprocess
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDelete(bookmark.bookmark_id); }} className="text-destructive">
                                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>

                {/* URL & Notes indicator */}
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                    {bookmark.url ? (
                        <>
                            <LinkIcon className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{new URL(bookmark.url).hostname}</span>
                        </>
                    ) : (
                        <>
                            <Type className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">Manual note</span>
                        </>
                    )}
                    {bookmark.notes && <StickyNote className="w-3 h-3 text-primary flex-shrink-0" />}
                    {bookmark.is_premium === false && (() => {
                        const reason = bookmark.downgraded_reason;
                        // V4 downgrade toast — show a specific reason when a bookmark
                        // was silently downgraded to Basic by the quota gates, so users
                        // understand WHY and can upgrade in one tap.
                        const reasonMeta = {
                            video_too_long: {
                                label: 'Basic · Too long',
                                title: `Video was ${bookmark.downgraded_meta?.duration_min?.toFixed?.(1) ?? '?'} min — over your tier's ${bookmark.downgraded_meta?.max_min ?? '?'} min cap. Upgrade for longer videos.`,
                                classes: 'bg-amber-50 text-amber-800 border-amber-200 cursor-pointer hover:bg-amber-100',
                            },
                            video_minutes_exhausted: {
                                label: 'Basic · Out of minutes',
                                title: `You've used all ${bookmark.downgraded_meta?.limit_min ?? '?'} video minutes this month. Upgrade for more.`,
                                classes: 'bg-amber-50 text-amber-800 border-amber-200 cursor-pointer hover:bg-amber-100',
                            },
                        };
                        const meta = reason && reasonMeta[reason];
                        if (meta) {
                            return (
                                <span
                                    className={`ml-auto inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide border ${meta.classes}`}
                                    title={meta.title}
                                    data-testid={`downgraded-badge-${bookmark.bookmark_id}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (typeof onUpgradeRequest === 'function') onUpgradeRequest(reason);
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            if (typeof onUpgradeRequest === 'function') onUpgradeRequest(reason);
                                        }
                                    }}
                                >
                                    {meta.label}
                                </span>
                            );
                        }
                        return (
                            <span
                                className="ml-auto inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-secondary text-muted-foreground border border-border"
                                title="Basic save - no AI summary. Reprocess to upgrade."
                                data-testid={`basic-badge-${bookmark.bookmark_id}`}
                            >
                                Basic
                            </span>
                        );
                    })()}
                </div>

                {/* Added by attribution for shared categories */}
                {bookmark.added_by_name && (
                    <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mb-2" data-testid={`added-by-${bookmark.bookmark_id}`}>
                        {bookmark.added_by_picture ? (
                            <img src={bookmark.added_by_picture} alt="" className="w-3.5 h-3.5 rounded-full" referrerPolicy="no-referrer" />
                        ) : (
                            <Users className="w-3 h-3" />
                        )}
                        <span>Added by {bookmark.added_by_name}</span>
                    </div>
                )}

                {/* Summary Preview */}
                {bookmark.summary && !hasQualityIssues(bookmark) && (
                    <p
                        className="text-xs text-muted-foreground line-clamp-2 mb-3"
                        dir={isHebrew(bookmark.summary) ? 'rtl' : 'ltr'}
                    >
                        {bookmark.summary}
                    </p>
                )}

                {/* Quality Issue Warning */}
                {hasQualityIssues(bookmark) && (
                    <div
                        className="flex items-start gap-2 p-2.5 rounded-xl bg-amber-50 border border-amber-200/60 mb-3"
                        data-testid={`quality-warning-${bookmark.bookmark_id}`}
                    >
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                            <p className="text-xs text-amber-800 leading-relaxed">{getQualityMessage(bookmark.quality_issues)}</p>
                        </div>
                        {bookmark.quality_issues?.includes('incomplete_text') ? (
                            <button
                                onClick={(e) => { e.stopPropagation(); onNavigate(`/bookmark/${bookmark.bookmark_id}?tab=transcript`); }}
                                className="flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-700 text-xs font-medium transition-colors flex-shrink-0"
                            >
                                <Edit2 className="w-3 h-3" />
                                Paste
                            </button>
                        ) : (
                            <button
                                onClick={(e) => { e.stopPropagation(); onReprocess(bookmark.bookmark_id); }}
                                className="flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-700 text-xs font-medium transition-colors flex-shrink-0"
                                data-testid={`reprocess-btn-${bookmark.bookmark_id}`}
                            >
                                <RefreshCw className="w-3 h-3" />
                                Retry
                            </button>
                        )}
                    </div>
                )}

                {/* Footer */}
                <div className="flex items-center justify-between pt-2.5 border-t border-border/60">
                    {/* Left: status badge (only for problems) or note preview */}
                    {getStatusBadge(bookmark.status, bookmark) || (
                        bookmark.notes ? (
                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground truncate max-w-[70%]" dir={isHebrew(bookmark.notes) ? 'rtl' : 'ltr'}>
                                <StickyNote className="w-3 h-3 text-primary flex-shrink-0" />
                                <span className="truncate">{bookmark.notes}</span>
                            </div>
                        ) : (
                            <CategoryChips bookmark={bookmark} bmCats={bmCats} />
                        )
                    )}
                    <span className="text-[10px] text-muted-foreground font-mono flex-shrink-0">
                        {formatDate(bookmark.created_at)}
                    </span>
                </div>

                {/* Tags row - sits beneath the category+date row. */}
                {bookmark.tags && bookmark.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                        {bookmark.tags.slice(0, 3).map((tag) => (
                            <span key={tag} className="px-2 py-0.5 rounded-full bg-secondary text-muted-foreground text-xs">
                                {tag}
                            </span>
                        ))}
                        {bookmark.tags.length > 3 && (
                            <span className="px-2 py-0.5 rounded-full bg-secondary text-muted-foreground text-xs">
                                +{bookmark.tags.length - 3}
                            </span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

// Category pills — show the first one in full, then up to 2 more as
// small icon-only chips (their color + glyph reads at a glance without
// burning horizontal space). Anything past 3 becomes a "+N" overflow
// chip. Falls back to an empty <span> when the bookmark has no
// category so the date still right-aligns via `justify-between`.
function CategoryChips({ bookmark, bmCats }) {
    if (!bmCats || bmCats.length === 0) return <span />;
    const FirstIcon = getCategoryIcon(bmCats[0].icon);
    const extras = bmCats.slice(1, 3);
    const overflow = bmCats.length - (1 + extras.length);
    return (
        <div className="flex items-center gap-1 min-w-0 flex-wrap">
            <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium font-body border"
                style={{
                    // Subtle outlined-pill style — matches BookmarkDetail.
                    borderColor: bmCats[0].color || '#10b981',
                    color: bmCats[0].color || '#10b981',
                    backgroundColor: `${bmCats[0].color || '#10b981'}10`,
                }}
                data-testid={`bookmark-category-label-${bookmark.bookmark_id}`}
            >
                <FirstIcon className="w-2.5 h-2.5" />
                <span className="truncate max-w-[120px]">{bmCats[0].name}</span>
            </span>
            {extras.map((extra) => {
                const ExtraIcon = getCategoryIcon(extra.icon);
                return (
                    <span
                        key={extra.category_id}
                        className="inline-flex items-center justify-center w-5 h-5 rounded-full border"
                        style={{
                            borderColor: extra.color || '#10b981',
                            color: extra.color || '#10b981',
                            backgroundColor: `${extra.color || '#10b981'}10`,
                        }}
                        title={extra.name}
                        data-testid={`bookmark-category-chip-${bookmark.bookmark_id}-${extra.category_id}`}
                    >
                        <ExtraIcon className="w-2.5 h-2.5" />
                    </span>
                );
            })}
            {overflow > 0 && (
                <span
                    className="px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground text-[10px] font-semibold"
                    title={bmCats.slice(3).map((c) => c.name).join(', ')}
                >
                    +{overflow}
                </span>
            )}
        </div>
    );
}
